import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../../lib/utils";

interface WebViewPopoverProps {
  children: React.ReactNode;
  content: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  sideOffset?: number;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const WebViewPopover: React.FC<WebViewPopoverProps> = ({
  children,
  content,
  side = "bottom",
  sideOffset = 8,
  className,
  open: controlledOpen,
  onOpenChange
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (onOpenChange) {
        onOpenChange(open);
      } else {
        setInternalOpen(open);
      }
    },
    [onOpenChange]
  );

  const updatePosition = () => {
    if (!triggerRef.current || !contentRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 16; // Minimum padding from viewport edges

    // If content hasn't been rendered yet (width/height are 0), use estimated dimensions
    const contentWidth = contentRect.width || 200; // Default width
    const contentHeight = contentRect.height || 100; // Default height

    let top = 0;
    let left = 0;

    // Calculate preferred position based on requested side
    switch (side) {
      case "top":
        top = triggerRect.top - contentHeight - sideOffset;
        left = triggerRect.left + triggerRect.width / 2 - contentWidth / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + sideOffset;
        left = triggerRect.left + triggerRect.width / 2 - contentWidth / 2;
        break;
      case "left":
        top = triggerRect.top + triggerRect.height / 2 - contentHeight / 2;
        left = triggerRect.left - contentWidth - sideOffset;
        break;
      case "right":
        top = triggerRect.top + triggerRect.height / 2 - contentHeight / 2;
        left = triggerRect.right + sideOffset;
        break;
    }

    // Ensure we don't start with invalid positions (0,0)
    if (top === 0 && left === 0) {
      // Fallback to trigger position if calculation fails
      top = triggerRect.bottom + sideOffset;
      left = triggerRect.left;
    }

    // Smart viewport-aware positioning
    const spaceAbove = triggerRect.top;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceLeft = triggerRect.left;
    const spaceRight = viewportWidth - triggerRect.right;

    // Check if we need to flip sides based on available space
    if (
      side === "top" &&
      spaceAbove < contentHeight + sideOffset &&
      spaceBelow > spaceAbove
    ) {
      // Flip to bottom if there's more space below
      top = triggerRect.bottom + sideOffset;
    } else if (
      side === "bottom" &&
      spaceBelow < contentHeight + sideOffset &&
      spaceAbove > spaceBelow
    ) {
      // Flip to top if there's more space above
      top = triggerRect.top - contentHeight - sideOffset;
    } else if (
      side === "left" &&
      spaceLeft < contentWidth + sideOffset &&
      spaceRight > spaceLeft
    ) {
      // Flip to right if there's more space on the right
      left = triggerRect.right + sideOffset;
    } else if (
      side === "right" &&
      spaceRight < contentWidth + sideOffset &&
      spaceLeft > spaceRight
    ) {
      // Flip to left if there's more space on the left
      left = triggerRect.left - contentWidth - sideOffset;
    }

    // Adjust horizontal position to stay within viewport
    if (left < padding) {
      left = padding;
    } else if (left + contentWidth > viewportWidth - padding) {
      left = viewportWidth - contentWidth - padding;
    }

    // Adjust vertical position to stay within viewport
    if (top < padding) {
      top = padding;
    } else if (top + contentHeight > viewportHeight - padding) {
      top = viewportHeight - contentHeight - padding;
    }

    // For narrow viewports, center the popover horizontally
    if (viewportWidth < 768) {
      left = Math.max(padding, (viewportWidth - contentWidth) / 2);
    }

    setPosition({ top, left });
    setIsPositioned(true);
  };

  useEffect(() => {
    if (isOpen) {
      setIsPositioned(false); // Reset positioning state

      // Use multiple attempts to ensure content is rendered
      const attemptPositioning = () => {
        if (contentRef.current) {
          const rect = contentRef.current.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            // Content is properly rendered, update position
            updatePosition();
          } else {
            // Content not ready yet, try again
            setTimeout(attemptPositioning, 10);
          }
        }
      };

      // Start positioning attempts
      requestAnimationFrame(attemptPositioning);

      const handleResize = () => updatePosition();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    } else {
      setIsPositioned(false); // Reset when closed
    }
  }, [isOpen, side, sideOffset]);

  // Attach click and mousedown handlers imperatively to the trigger element
  // to avoid SonarQube warnings about interactive handlers on non-interactive elements
  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const handleTriggerClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(!isOpen);
    };

    const handleTriggerMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    trigger.addEventListener("click", handleTriggerClick);
    trigger.addEventListener("mousedown", handleTriggerMouseDown);

    return () => {
      trigger.removeEventListener("click", handleTriggerClick);
      trigger.removeEventListener("mousedown", handleTriggerMouseDown);
    };
  }, [isOpen, setIsOpen]);

  // Handle clicks outside to close and manage dialog overlay pointer events
  useEffect(() => {
    if (!isOpen) return;

    // Only disable pointer events on dialog overlays (not content) when popover is open
    const dialogOverlays = document.querySelectorAll(
      "[data-radix-dialog-overlay]"
    );

    dialogOverlays.forEach((overlay) => {
      (overlay as HTMLElement).style.pointerEvents = "none";
    });

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const targetElement = target as HTMLElement;

      // Don't close if clicking within the popover content
      if (contentRef.current?.contains(target)) {
        return;
      }

      // Close if clicking on another date picker trigger
      if (targetElement.closest('[data-eds-date-picker="true"]')) {
        setIsOpen(false);
        return;
      }

      // Close if clicking outside both trigger and content
      if (
        triggerRef.current &&
        contentRef.current &&
        !triggerRef.current.contains(target) &&
        !contentRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    // WCAG 2.1.1 – allow keyboard dismissal via Escape and return focus
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    // Use a slight delay to allow calendar clicks to process first
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);

      // Restore pointer events when popover closes
      dialogOverlays.forEach((overlay) => {
        (overlay as HTMLElement).style.pointerEvents = "";
      });
    };
  }, [isOpen, setIsOpen]);

  return (
    <>
      <div
        ref={triggerRef}
        style={{
          cursor: "pointer",
          position: "relative",
          zIndex: 1,
          background: "none",
          border: "none",
          padding: 0
        }}
      >
        {children}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={contentRef}
            className={cn(
              "fixed rounded-md border bg-popover text-popover-foreground shadow-md outline-none",
              "webview-popover-dropdown", // Custom class for styling
              isPositioned ? "animate-in fade-in-0 zoom-in-95" : "opacity-0",
              className
            )}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              minWidth: "200px",
              maxWidth: window.innerWidth < 768 ? "90vw" : "300px",
              width: "auto",
              zIndex: 2147483647, // Use maximum possible z-index
              boxShadow:
                "var(--shadow-md, 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1))",
              position: "fixed",
              pointerEvents: "auto"
            }}
            onMouseDown={(e) => {
              // Only prevent propagation, don't prevent default
              // This allows calendar to work but stops dialog interference
              e.stopPropagation();
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};
